import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import * as accountModal from "accountModal";
import * as browserTTS from "browserTTS";
import loadHandwritingFont from "loadHandwritingFont";
import * as modalDialog from "modalDialog";
import models from "models/models";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as sharedConfig from "shared/sharedConfig";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as utils from "shared/utils";
import { cn } from "shared/utils";
import * as spanUtils from "spanUtils";
import ChatContainerView from "views/ChatContainerView";
import PracticePlayTTSView from "views/PracticePlayTTSView";
import TTSVoicePickerView from "views/TTSVoicePickerView";
import TranslatableTextView from "views/TranslatableTextView";
import VocabSelector from "views/VocabSelector";

type Props = {
  appRouter: any;
  options?: {
    userChatID?: string;
    start?: boolean;
    initialMessageID?: string;
  };
};

interface CorrectionItem {
  correctedText: string;
  originalText?: string;
  translation?: string;
  toSave?: boolean;
}

interface UserMessage {
  type: "user";
  text: string;
  coherence?: number; // -1 (confusing), 0 (neutral), or 1 (perfectly flowing)
  correctedText?: string;
  correctionDiff?: CorrectionItem[];
  usedVocab?: string[];
  praiseMessage?: string;
}

interface BotMessage {
  type: "bot";
  text: string;
  // Idea: add an emotion to be used for the bot's avatar
}

type Message = BotMessage | UserMessage;

interface LoadedState {
  type: "loaded";
  userChatID: string;
  language: string;
  messages: Message[];
  currentMessage?: {
    text: string;
    autoComplete?: {
      lastSentence: string;
      suggestions: { text: string; translation: string }[];
    };
  };
  completed: boolean;
  showEndOfChatInfo: boolean;
  // All diffs which have been clicked during post chat review
  // key is ${messageIndex}-${diffIndex}
  reviewedDiffs: { [key: string]: boolean };
  phraseIndex: {
    [firstWord: string]: {
      whole: string;
      split: string[];
    }[];
  };
  selectedCorrection?: {
    messageIndex: number;
    correctionIndex: number;
    alternativeCorrections?: {
      text: string;
      translation: string;
    }[];
  };
  ttsVoices: { voiceURI: string; displayName: string }[];
}

type State =
  | {
      type: "loading";
    }
  | LoadedState;

class ChatPageView extends preact.Component<Props, State> {
  textareaRef: any; // preact.RefObject<HTMLTextAreaElement>;
  unmounted: boolean = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      type: "loading",
    };

    // Set body properties so that the highlighting CSS designed for readerPage also works here
    $("body").attr("data-clickMode", "translate");
    $("body").addClass("glossStyleNew");

    loadHandwritingFont();

    // Load the chat
    if (this.props.options.userChatID) {
      this.loadChat();
    } else if (this.props.options.start) {
      this.start();
    }

    window.document.addEventListener("click", this.documentClickHandler);
    models.user.on("change:speakingMode", this.checkTTSSupport);
  }

  correctionMenuClassName = "correctionMenu";

  documentClickHandler = (event: MouseEvent) => {
    // If the user clicks outside the correction menu, close it
    const target = event.target as Element;
    if (
      this.state.type === "loaded" &&
      this.state.selectedCorrection &&
      !target.closest(`.${this.correctionMenuClassName}`)
    ) {
      this.setState({
        selectedCorrection: undefined,
      });
    }
  };

  async loadChat() {
    let userChat;

    try {
      userChat = (
        await readlangClient.request(
          "GET",
          `/api/chat/${this.props.options.userChatID}`,
          {}
        )
      ).data.userChat;
    } catch (error) {
      console.error("Error loading chat: ", error);
      alert(error.jqXHR?.data?.error ?? "Error loading chat");
      this.props.appRouter.navigate("/chat", { trigger: true });
      return;
    }

    const completed = userChat.summary !== undefined;
    await this.setState({
      type: "loaded",
      messages: userChat.messages.map((message) => ({
        type: message.person,
        text: message.text,
        correctionDiff: message.correctionDiff.map((diffItem) => ({
          ...diffItem,
          toSave:
            diffItem.originalText !== undefined && diffItem.translation
              ? true
              : false,
        })),
      })),
      currentMessage: undefined,
      reviewedDiffs: {},
      phraseIndex: {},
      userChatID: userChat._id,
      completed,
      showEndOfChatInfo: completed,
      language: userChat.language,
      ttsVoices: [],
    });

    // Set the user's learning language
    if (models.user.get("currentLearningLanguage") !== userChat.language) {
      models.user.save({ currentLearningLanguage: userChat.language });
    }

    if (languageMap.map[userChat.language].alphabet) {
      $("body").removeClass("basicLatinFirstLanguage");
    } else {
      $("body").addClass("basicLatinFirstLanguage");
    }

    this.fetchPhraseIndex();

    this.props.appRouter.navigate(`/chat/${userChat._id}`);

    await this.checkTTSSupport();
  }

  // This is intentionally using arrow functions so that it can be used as an on change handler
  checkTTSSupport = async () => {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    if (!models.user.get("speakingMode")) {
      return;
    }

    const { ttsVoices, voiceSelected } = await browserTTS.initialize(
      this.state.language
    );

    await this.setState({
      ttsVoices,
    });

    if (!voiceSelected) {
      browserTTS.cancelTTS();
      modalDialog.showPreactComponent(
        <TTSVoicePickerView
          title="Select a voice for Ami"
          ttsVoices={ttsVoices}
          language={this.state.language}
          close={() => {
            modalDialog.cancelAll();
          }}
        />
      );
    }
  };

  async start() {
    await this.setState({ type: "loading" });

    const language = models.user.get("currentLearningLanguage");

    if (!models.user.hasChatFeature()) {
      // User doesn't have access to the chat feature
      this.props.appRouter.navigate("/learn", { trigger: true });
      return;
    }

    let newMessage, userChatID;

    try {
      const response = await readlangClient.request("POST", "/api/chat/start", {
        language,
        initialMessageID: this.props.options?.initialMessageID,
      });
      ({ newMessage, userChatID } = response.data);
    } catch (error) {
      console.error("Failed to start chat:", error);
      // You can handle the error or rethrow if needed
      if (models.user.hasPremiumPlusAccess()) {
        alert(
          "You've reached the daily limit of " +
            subscriptionPlans.permissions.plus.chat +
            " conversations per day intended to " +
            "prevent abuse of the system. Contact steve@readlang.com if you think this " +
            "has happened in error."
        );
      }
      this.upgrade();
      return;
    }

    await this.setState({
      type: "loaded",
      messages: [
        {
          type: "bot",
          text: newMessage.text,
        },
      ],
      currentMessage: undefined,
      reviewedDiffs: {},
      phraseIndex: {},
      userChatID,
      language,
      ttsVoices: [],
    });

    this.fetchPhraseIndex();

    this.props.appRouter.navigate(`/chat/${userChatID}`);

    await this.checkTTSSupport();

    this.playTTSIfAppropriate(newMessage.text);
  }

  upgrade() {
    accountModal.show({
      model: models.user,
      title: "chatLimitReached",
      messageHtml:
        "<div>" +
        `<h2>You've reached your ${subscriptionPlans.permissions.standard.chat} chats per day limit</h2>` +
        "<p>Upgrade for unlimited conversations!</p>" +
        "</div>",
      showTitle: false,
      smartDefinitions: true,
      plus: true,
      onCancel: this.onCancelUpgrade,
    });
  }

  onCancelUpgrade = () => {
    // If the component has been unmounted, do nothing. This happens if the user clicked the button
    // in the account modal to go to the upgrade page, which navigates away from this page.
    if (this.unmounted) {
      return;
    }
    this.props.appRouter.navigate("/chat", { trigger: true });
  };

  async fetchPhraseIndex() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const phraseIndex = await models.allPhrasesIndex.fetch({
      language: this.state.language,
      filter: "readyToLearn",
    });

    /** Normalize the key so that accent and capitalization differences aren't counted */
    const normalizedPhraseIndex: { [key: string]: any } = {};

    for (const key of Object.keys(phraseIndex)) {
      // Not normalizing accents, since we will be comparing against the corrected text which
      // should have the correct accents
      const normalizedKey = utils.normalizeString(key, false);

      if (!normalizedPhraseIndex[normalizedKey]) {
        normalizedPhraseIndex[normalizedKey] = [];
      }

      normalizedPhraseIndex[normalizedKey] = [
        ...normalizedPhraseIndex[normalizedKey],
        ...phraseIndex[key],
      ];
    }

    this.setState({ phraseIndex: normalizedPhraseIndex });
  }

  async fetchNextMessage() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const response = await readlangClient.request("POST", "/api/chat/message", {
      messages: this.state.messages.map((message) => ({
        person: message.type,
        text: message.text,
        correctionDiff:
          message.type === "user" ? message.correctionDiff : undefined,
        correctedText:
          message.type === "user" ? message.correctedText : undefined,
      })),
      language: this.state.language,
      userChatID: this.state.userChatID,
    });

    const endOfChatStats = response.data.endOfChatStats;

    await this.setState({
      ...this.state,
      messages: [
        ...this.state.messages,
        {
          type: "bot",
          text: response.data.newMessage.text,
        },
      ],
      currentMessage: {
        text: "",
      },
      completed: endOfChatStats !== undefined,
    });

    if (endOfChatStats) {
      // After 3 seconds, show the end of chat info
      setTimeout(() => {
        this.setState({
          showEndOfChatInfo: true,
        });
      }, 3000);
    }

    this.playTTSIfAppropriate(response.data.newMessage.text);

    // Wait for the textarea to be rendered so that it's not disabled
    setTimeout(() => {
      if (this.state.type !== "loaded") {
        throw new Error("Unexpected state");
      }
      if (!this.state.completed) {
        this.textareaRef.focus();
      }
    }, 100);
  }

  playTTSIfAppropriate(text: string) {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    if (!this.state.ttsVoices || this.state.ttsVoices.length === 0) {
      // TTS is not supported, so don't play it
      return;
    }

    // Filter out emojis from the text
    const filteredText = utils.filterOutEmojis(text);

    // Speak the bot message
    if (models.user.get("speakingMode")) {
      browserTTS.playTTSSimple(this.state.language, filteredText);
    }
  }

  async successfullyUsedReadyToLearnVocab(word: string) {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    readlangClient.request("POST", "/api/chat/successfullyUsedVocab", {
      language: this.state.language,
      word,
    });

    // Remove the word from the phrase index
    const newPhraseIndex = models.allPhrasesIndex.wordRecalled(
      models.user.get("currentLearningLanguage"),
      word
    );

    this.setState({
      phraseIndex: newPhraseIndex,
    });
  }

  progress() {
    if (this.state.type === "loading") {
      return 0;
    }

    if (this.state.type !== "loaded") {
      throw new Error("Unexpected state");
    }

    return this.state.completed
      ? 1
      : this.state.messages.length /
          Math.max(8 * 2, this.state.messages.length + 1);
  }

  render() {
    const languageString = languageMap.map[this.language()].english;
    const firstLanguageString =
      languageMap.map[models.user.get("firstLanguage")].english;

    return (
      <ChatContainerView
        progress={this.progress()}
        onClose={() => {
          browserTTS.cancelTTS();
          this.props.appRouter.navigate("/chat", { trigger: true });
        }}
        ttsVoices={this.state.type === "loaded" ? this.state.ttsVoices : []}
        language={
          this.state.type === "loaded" ? this.state.language : undefined
        }
      >
        <>
          <p class="chatIntro">
            If you don't know how to say something in {languageString}, feel
            free to use the occasional{" "}
            {languageString === "English"
              ? firstLanguageString
              : `English${
                  firstLanguageString !== "en"
                    ? ` or ${firstLanguageString}`
                    : ""
                }`}{" "}
            word or phrase.
          </p>
        </>
        {this.state.type === "loaded" ? this.renderChat() : "Loading..."}
      </ChatContainerView>
    );
  }

  language() {
    return this.state.type === "loaded"
      ? this.state.language
      : models.user.get("currentLearningLanguage");
  }

  renderChat() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const messages = this.state.type === "loaded" ? this.state.messages : [];
    const completed = this.state.type === "loaded" && this.state.completed;

    // Add negative reactions as extra bot messages with confused emoji
    if (messages.length > 0) {
      const lastUserMessage = _.last(messages) as UserMessage;
      if (lastUserMessage.coherence !== undefined) {
        if (lastUserMessage.coherence < 0) {
          this.setState({
            messages: [
              ...messages,
              {
                type: "bot",
                text: "🤔",
              },
            ],
          });
        }
      }
    }

    const languageString = languageMap.map[this.language()].english;

    /** True if there's at least on correction */
    let hasCorrection = false;

    const vocabList = this.vocab();

    return (
      <>
        <div class={`chatStream${completed ? " endOfChat" : ""}`}>
          {messages.map((message, messageIndex) => {
            if (this.state.type !== "loaded") {
              throw new Error("Chat not started");
            }

            return message.type === "bot" ? (
              <div class="botMessage">
                <div class="botName">Ami</div>
                <td>
                  {this.state.ttsVoices.length > 0 ? (
                    <PracticePlayTTSView
                      text={utils.filterOutEmojis(message.text)}
                      language={this.language()}
                      forceBrowserTTS={true}
                    />
                  ) : null}
                  <TranslatableTextView
                    text={message.text}
                    sourceLanguage={this.language()}
                    targetLanguage={models.user.get("firstLanguage")}
                    afterUpdate={
                      messageIndex === messages.length - 1
                        ? () => {
                            this.scrollToBottom();
                          }
                        : undefined
                    }
                    source={{ userChatID: this.state.userChatID }}
                  />
                </td>
              </div>
            ) : (
              <div class="userMessageContainer">
                <div class="userMessage">
                  <div class="userName">You</div>
                  <div class="messageBody">
                    {message.correctionDiff
                      ? message.correctionDiff.map(
                          (correction, correctionIndex) => {
                            if (this.state.type !== "loaded") {
                              throw new Error("Chat not started");
                            }

                            const isSelected =
                              this.state.selectedCorrection &&
                              this.state.selectedCorrection.messageIndex ===
                                messageIndex &&
                              this.state.selectedCorrection.correctionIndex ===
                                correctionIndex;

                            if (
                              correction.originalText &&
                              correction.translation
                            ) {
                              hasCorrection = true;
                            }

                            return (
                              <>
                                {correction.originalText ? (
                                  <>
                                    <span class="originalText">
                                      {correction.originalText}
                                    </span>{" "}
                                  </>
                                ) : null}
                                {correction.translation ? (
                                  <span
                                    class={cn({
                                      translating: true,
                                      translated: true,
                                      selected: isSelected,
                                    })}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (isSelected) {
                                        this.setState({
                                          selectedCorrection: undefined,
                                        });
                                      } else {
                                        this.setState({
                                          selectedCorrection: {
                                            messageIndex,
                                            correctionIndex,
                                          },
                                        });
                                      }
                                    }}
                                  >
                                    <div class="translation">
                                      {correction.translation}
                                    </div>
                                    <div class="original">
                                      {correction.correctedText}
                                    </div>
                                    {isSelected ? (
                                      <div class={this.correctionMenuClassName}>
                                        {this.state.selectedCorrection
                                          .alternativeCorrections ? (
                                          <div>
                                            <h3>
                                              Pick from these alternatives
                                            </h3>

                                            <table>
                                              {this.state.selectedCorrection.alternativeCorrections.map(
                                                (alternativeCorrection) => (
                                                  <tr
                                                    onClick={() =>
                                                      this.acceptAlternativeCorrection(
                                                        alternativeCorrection
                                                      )
                                                    }
                                                  >
                                                    <td>
                                                      {
                                                        alternativeCorrection.text
                                                      }
                                                    </td>
                                                    <td class="alternativeTranslation">
                                                      {
                                                        alternativeCorrection.translation
                                                      }
                                                    </td>
                                                    {/* TODO: Add original text too */}
                                                  </tr>
                                                )
                                              )}
                                            </table>
                                          </div>
                                        ) : (
                                          <ul>
                                            <li>
                                              <button
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  this.undoCorrection();
                                                }}
                                              >
                                                Undo Correction
                                              </button>
                                            </li>
                                            {/* <li>Undo Correction</li> */}
                                            <li>
                                              <button
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  this.disputeCorrection();
                                                }}
                                              >
                                                Dispute Correction
                                              </button>
                                            </li>
                                          </ul>
                                        )}
                                      </div>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span>{correction.correctedText}</span>
                                )}
                              </>
                            );
                          }
                        )
                      : message.text}

                    {message.usedVocab?.length > 0 ? (
                      <div class="feedback">
                        Boosted vocab: {message.usedVocab.join(", ")}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                {message.praiseMessage ? (
                  <div class="praiseMessage">
                    <i class="icon-star"></i> {message.praiseMessage}
                  </div>
                ) : null}
              </div>
            );
          })}
          {_.last(messages)?.type !== "bot" ? (
            <div class="botMessage">
              <div class="botName">Ami</div>
              <div class="messageBody">
                <span class="waitingMessage">thinking</span>
              </div>
            </div>
          ) : null}
        </div>
        {!completed ? (
          <div class="chatInputContainerContainer">
            <div class="chatInputContainer">
              <div class="chatInputWrapper">
                {this.state.type === "loaded" &&
                this.state.currentMessage?.autoComplete ? (
                  <ul class="autoComplete">
                    {this.state.currentMessage.autoComplete.suggestions.map(
                      (suggestion) => (
                        <li
                          class="autoCompleteSuggestion"
                          onClick={this.acceptAutoCompletion(suggestion.text)}
                        >
                          {suggestion.text}{" "}
                          <small>({suggestion.translation})</small>
                        </li>
                      )
                    )}
                  </ul>
                ) : null}
                <textarea
                  ref={(el) => (this.textareaRef = el)}
                  class={cn({
                    chatInput: true,
                    invalid:
                      this.state.currentMessage?.text.length >
                      sharedConfig.maxChatMessageLength,
                  })}
                  placeholder={`Type your message in ${languageString} here...`}
                  lang={this.language()}
                  value={
                    this.state.type === "loaded"
                      ? this.state.currentMessage?.text ?? ""
                      : ""
                  }
                  onInput={async (e) => {
                    if (this.state.type !== "loaded") {
                      throw new Error("Unexpected state");
                    }
                    const input = e.target as HTMLTextAreaElement;

                    await this.setState({
                      currentMessage: {
                        ...this.state.currentMessage,
                        text: input.value,
                      },
                    });

                    // Disable auto-complete feature
                    // this.fetchAutoComplete();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      this.handleSendMessage();
                    }
                  }}
                  disabled={_.last(messages)?.type !== "bot"}
                  autoFocus={_.last(messages)?.type === "bot"}
                />
                <button
                  class="prominent"
                  disabled={
                    !(this.state.type === "loaded" && this.state.currentMessage)
                  }
                  onClick={() => this.handleSendMessage()}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {this.state.showEndOfChatInfo ? (
          <div>
            <h2 class="chatComplete">🎉 Chat complete! 🎉</h2>
            {hasCorrection ? (
              <VocabSelector
                appRouter={this.props.appRouter}
                vocab={vocabList}
                userChatID={this.state.userChatID}
                selectAll={(value: boolean) => {
                  if (this.state.type !== "loaded") {
                    throw new Error("Chat not started");
                  }

                  this.setState({
                    messages: this.state.messages.map((message) => {
                      if (message.type === "user") {
                        return {
                          ...message,
                          correctionDiff: message.correctionDiff?.map((diff) =>
                            diff.originalText !== undefined && diff.translation
                              ? {
                                  ...diff,
                                  toSave: value,
                                }
                              : diff
                          ),
                        };
                      }
                      return message;
                    }),
                  });
                }}
                onToggle={(index: number) => {
                  if (this.state.type !== "loaded") {
                    throw new Error("Chat not started");
                  }

                  const vocabItem = vocabList[index];
                  this.setState(
                    {
                      messages: this.state.messages.map(
                        (message, messageIndex) => {
                          if (
                            message.type === "user" &&
                            messageIndex === vocabItem.messageIndex
                          ) {
                            return {
                              ...message,
                              correctionDiff: message.correctionDiff?.map(
                                (diff, diffIndex) => {
                                  if (diffIndex === vocabItem.diffItemIndex) {
                                    return {
                                      ...diff,
                                      toSave: !diff.toSave,
                                    };
                                  }
                                  return diff;
                                }
                              ),
                            };
                          }
                          return message;
                        }
                      ),
                    },
                    () => {
                      console.log("new state", this.state);
                    }
                  );
                }}
              />
            ) : null}
            <p>
              <button
                onClick={() =>
                  this.props.appRouter.navigate("/chat", { trigger: true })
                }
              >
                Return to conversation list
              </button>
            </p>
            <h2>How did you enjoy this chat?</h2>
            <p>
              Please leave feedback on this feature{" "}
              <a
                target="_blank"
                href="https://forum.readlang.com/t/new-conversational-chatbot-for-language-learning/2744"
              >
                here in the forum
              </a>
              .
            </p>
          </div>
        ) : null}
      </>
    );
  }

  acceptAutoCompletion = (completion: string) => () => {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    if (!this.state.currentMessage?.autoComplete) {
      throw new Error("No current message");
    }

    this.setState({
      currentMessage: {
        ...this.state.currentMessage,
        text: this.state.currentMessage.text.replace(
          this.state.currentMessage.autoComplete.lastSentence,
          completion
        ),
        autoComplete: undefined,
      },
    });
  };

  async fetchAutoComplete() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const input = this.state.currentMessage?.text;

    if (!input) {
      return;
    }

    // Remove existing autoComplete
    await this.setState({
      currentMessage: {
        ...this.state.currentMessage,
        autoComplete: undefined,
      },
    });

    await utils.sleep(500);

    if (this.state.currentMessage?.text !== input) {
      // User has changed the input since this function was called
      return;
    }

    // Extract the last sentence by splitting on sentence terminators
    let lastSentence = (() => {
      const sentences = input
        .split(/([.!?])\s+/)
        .filter((s) => s.trim().length > 0);
      return sentences[sentences.length - 1];
    })();

    // If last sentence is longer than 50 characters, use only the last 50-ish characters, making
    // sure to split it on a word boundary
    // const maxLength = 25;
    // if (lastSentence.length > maxLength) {
    //   let potentialSplitPoint = lastSentence.length - maxLength;
    //   while (
    //     potentialSplitPoint < lastSentence.length - 1 &&
    //     !lastSentence[potentialSplitPoint].match(/\s/)
    //   ) {
    //     potentialSplitPoint++;
    //   }
    //   lastSentence = lastSentence.slice(potentialSplitPoint + 1);
    // }

    // If the last sentence doesn't contain any word characters, don't show the autoComplete
    if (!spanUtils.wordRegExp().test(lastSentence)) {
      return;
    }

    const suggestions = (
      await readlangClient.request("POST", "/api/chat/autoComplete", {
        language: this.state.language,
        text: lastSentence,
      })
    ).data.suggestions;

    console.log("autocomplete response: ", suggestions);

    if (suggestions.length === 0) {
      // No completions, so don't show the autoComplete
      return;
    }

    if (this.state.currentMessage?.text !== input) {
      // User has changed the input since the request was made
      return;
    }

    // Populate the autoComplete list
    this.setState({
      currentMessage: {
        ...this.state.currentMessage,
        autoComplete: {
          lastSentence,
          suggestions,
        },
      },
    });
  }

  disputeCorrection = async () => {
    const disputePrompt = prompt(
      "Please explain what you were trying to say here."
    );

    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    if (!this.state.selectedCorrection) {
      throw new Error("No correction selected");
    }

    const { messageIndex, correctionIndex } = this.state.selectedCorrection;

    const message = this.state.messages[messageIndex] as UserMessage;
    const correction = message.correctionDiff?.[correctionIndex];
    if (!correction) {
      throw new Error("No correction found");
    }

    const alternativeCorrections = (
      await readlangClient.request("POST", "/api/chat/disputeCorrection", {
        language: this.state.language,
        message: message.text,
        originalText: correction.originalText,
        correctedText: correction.correctedText,
        disputePrompt,
      })
    ).data.alternativeCorrections;

    if (
      this.state.selectedCorrection?.messageIndex !== messageIndex ||
      this.state.selectedCorrection?.correctionIndex !== correctionIndex
    ) {
      // The selected correction changed, so we don't want to update the state
      // with the new corrections
      return;
    }

    await this.setState({
      selectedCorrection: {
        ...this.state.selectedCorrection,
        alternativeCorrections,
      },
    });
  };

  undoCorrection = async () => {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const { messageIndex, correctionIndex } = this.state.selectedCorrection;

    if (!this.state.selectedCorrection) {
      throw new Error("No correction selected");
    }

    await this.setState({
      selectedCorrection: undefined,
      messages: this.state.messages.map((message, index) => {
        if (index === messageIndex && message.type === "user") {
          return {
            ...message,
            correctionDiff: message.correctionDiff?.map((diff, diffIndex) => {
              if (diffIndex === correctionIndex) {
                return {
                  correctedText: diff.originalText,
                };
              }
              return diff;
            }),
          };
        }
        return message;
      }),
    });

    this.saveMessages();
  };

  acceptAlternativeCorrection = async (alternativeCorrection: {
    text: string;
    translation: string;
  }) => {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const { messageIndex, correctionIndex } = this.state.selectedCorrection;

    if (!alternativeCorrection) {
      throw new Error("No alternative correction found");
    }

    await this.setState({
      selectedCorrection: undefined,
      messages: this.state.messages.map((message, index) => {
        if (index === messageIndex && message.type === "user") {
          return {
            ...message,
            correctionDiff: message.correctionDiff?.map((diff, diffIndex) => {
              if (diffIndex === correctionIndex) {
                if (diff.originalText === alternativeCorrection.text) {
                  // Undo correction, as if it was never corrected
                  return {
                    correctedText: alternativeCorrection.text,
                  };
                }
                return {
                  ...diff,
                  correctedText: alternativeCorrection.text,
                  translation: alternativeCorrection.translation,
                };
              }
              return diff;
            }),
          };
        }
        return message;
      }),
    });

    this.saveMessages();
  };

  async saveMessages() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    await readlangClient.request("POST", "/api/chat/editMessages", {
      userChatID: this.state.userChatID,
      messages: this.state.messages.map((message) => ({
        person: message.type,
        text: message.text,
        correctionDiff:
          message.type === "user" ? message.correctionDiff : undefined,
        correctedText:
          message.type === "user" ? message.correctedText : undefined,
      })),
    });
  }

  vocab() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    /** Keep track of added vocab to avoid adding duplicate identical word-translation pairs */
    const added = new Set<string>();
    const shouldAdd = (word: string, translation: string) => {
      const key = `${word}-${translation}`;
      if (added.has(key)) {
        return false;
      }
      added.add(key);
      return true;
    };

    return _.flatten(
      this.state.messages.map((message, messageIndex) => {
        if (message.type !== "user") {
          return [];
        }
        return message.correctionDiff
          ? message.correctionDiff
              .map((diffItem, diffItemIndex) =>
                diffItem.originalText !== undefined &&
                diffItem.correctedText &&
                diffItem.translation &&
                shouldAdd(diffItem.correctedText, diffItem.translation)
                  ? {
                      word: diffItem.correctedText,
                      translation: diffItem.translation,
                      context: utils.findContextSentence(
                        message.correctionDiff
                          .map((correction) => correction.correctedText)
                          .join(""),
                        diffItem.correctedText
                      ),
                      toSave: diffItem.toSave,
                      messageIndex,
                      diffItemIndex,
                    }
                  : undefined
              )
              .filter((diffItem) => diffItem !== undefined)
          : [];
      })
    );
  }

  logWordsRead(wordsRead: number) {
    // Send tracking info
    readlangClient.request("POST", "/api/userLearningLog/read", {
      wordsRead,
      language: models.user.get("currentLearningLanguage"),
      timezoneOffset: dateUtils.getTimezoneOffset(),
    });
  }

  logWordsPracticed(wordsPracticed: number) {
    readlangClient.request("POST", "/api/userLearningLog/practiced", {
      wordsPracticed,
      language: models.user.get("currentLearningLanguage"),
      timezoneOffset: dateUtils.getTimezoneOffset(),
    });
  }

  async gradeCoherence() {
    if (this.state.type !== "loaded") {
      throw new Error("Not started");
    }

    const response = await readlangClient.request(
      "POST",
      "/api/chat/gradeCoherence",
      {
        messages: this.state.messages,
        language: this.state.language,
      }
    );

    const lastUserMessage = _.last(this.state.messages) as UserMessage;

    const updatedMessage = {
      ...lastUserMessage,
      coherence: response.data.coherence,
    };

    // Generate praise message if appropriate (now that we have coherence score)
    if (this.shouldShowPraise(updatedMessage)) {
      updatedMessage.praiseMessage = this.getRandomPraiseMessage();
    }

    const messages = [...this.state.messages.slice(0, -1), updatedMessage];

    this.setState({
      messages,
    });
  }

  getRandomPraiseMessage(): string {
    const praiseMessages = [
      "Good response!",
      "Nicely done!",
      "Great job!",
      "Excellent!",
      "Fantastic work!",
      "Outstanding response!",
    ];
    return praiseMessages[Math.floor(Math.random() * praiseMessages.length)];
  }

  shouldShowPraise(message: UserMessage): boolean {
    if (!message.correctionDiff) {
      return false;
    }

    // Check if the message is long enough (at least 40 characters without whitespace)
    const textLength = message.text.replace(/\s/g, "").length;
    if (textLength < 40) {
      return false;
    }

    // Only show praise for coherent messages (coherence 0 or 1)
    if (message.coherence !== undefined && message.coherence < 0) {
      return false;
    }

    // Check if there are no significant corrections
    const hasSignificantCorrections = message.correctionDiff.some(
      (diff) =>
        diff.originalText !== undefined && diff.translation !== undefined
    );

    return !hasSignificantCorrections;
  }

  async correctText() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const lastUserMessage = _.last(this.state.messages) as UserMessage;

    if (!lastUserMessage || lastUserMessage.type !== "user") {
      throw new Error("Unexpected state");
    }

    const lastButOneUserMessage = this.state.messages[
      this.state.messages.length - 2
    ] as UserMessage;

    const correctionDiff = (
      await readlangClient.request("POST", "/api/chat/correctText", {
        text: lastUserMessage.text,
        language: models.user.get("currentLearningLanguage"),
        previousText: lastButOneUserMessage?.text,
      })
    ).data.correctionDiff;

    const usedVocab = [];
    // Look for the first word in the phrase index
    for (const diffPart of correctionDiff) {
      if (diffPart.originalText !== undefined) {
        // Don't highlight this one, since user didn't get it correct
        continue;
      }

      // TODO: use better split algo that accounts for punctuation
      //       see how spannify does it
      for (const word of diffPart.correctedText.split(utils.notWordRegExp())) {
        const phraseIndexEntries =
          this.state.phraseIndex[utils.normalizeString(word, true)];

        if (!phraseIndexEntries) {
          continue;
        }

        for (const phraseIndexEntry of phraseIndexEntries) {
          // Check if the WHOLE phrase appears
          if (
            utils
              .normalizeString(diffPart.correctedText, true)
              .indexOf(utils.normalizeString(phraseIndexEntry.whole, true)) !==
            -1
          ) {
            usedVocab.push(phraseIndexEntry.whole);
            this.successfullyUsedReadyToLearnVocab(phraseIndexEntry.whole);
            break;
          }
        }
      }
    }

    const updatedMessage = {
      ...lastUserMessage,
      correctionDiff: correctionDiff.map((diffItem) => ({
        ...diffItem,
        toSave:
          diffItem.originalText !== undefined && diffItem.translation
            ? true
            : false,
      })),
      // Keep record of the raw corrected text just in case we want to refer back to it
      correctedText: correctionDiff
        .map((diffPart) => diffPart.correctedText)
        .join(""),
      usedVocab,
    };

    // Note: praise message will be generated after coherence grading

    const messages = [...this.state.messages.slice(0, -1), updatedMessage];

    this.setState({
      messages,
    });
  }

  handleSendMessage() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    if (!this.state.currentMessage?.text) {
      console.error("no user message to send");
      return;
    }

    // Limit length of message
    if (
      this.state.currentMessage?.text.length > sharedConfig.maxChatMessageLength
    ) {
      alert(
        `Your message is too long. Please limit it to ${sharedConfig.maxChatMessageLength} characters.`
      );
      return;
    }

    const newMessage: UserMessage = {
      type: "user",
      text: this.state.currentMessage.text,
    };
    this.setState(
      {
        messages: [...this.state.messages, newMessage],
        currentMessage: undefined,
      },
      async () => {
        try {
          await this.correctText();
          await this.gradeCoherence();
        } catch (error) {
          console.error(error);
          alert(
            "Error processing message: " + error?.jqXHR?.data?.error ??
              "unknown error"
          );
          return;
        }

        // Intentionally not awaiting this
        this.logLearningProgress();

        try {
          await this.fetchNextMessage();
        } catch (error) {
          console.error(error);
          alert(
            "Error fetching next message: " + error?.jqXHR?.data?.error ??
              "unknown error"
          );
          return;
        }
      }
    );
  }

  async logLearningProgress() {
    if (this.state.type !== "loaded") {
      throw new Error("Chat not started");
    }

    const lastUserMessage = _.last(
      this.state.messages.filter((message) => message.type === "user")
    ) as UserMessage;
    const lastBotMessage = _.last(
      this.state.messages.filter((message) => message.type === "bot")
    ) as BotMessage;

    if (lastUserMessage.coherence < 0) {
      // Don't log progress for incoherent messages
      return;
    }

    const wordsPracticed = lastUserMessage.text
      .split(utils.notWordRegExp())
      .filter((word) => word.length > 0).length;
    const wordsRead = lastBotMessage.text
      .split(utils.notWordRegExp())
      .filter((word) => word.length > 0).length;

    await this.logWordsPracticed(Math.round(wordsPracticed / 10));
    await this.logWordsRead(wordsRead);
    models.streak.fetch();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const previousMessages =
      prevState.type === "loaded" ? prevState.messages : [];

    if (
      this.state.type === "loaded" &&
      prevState.type !== "loading" &&
      (this.state.messages.length > previousMessages.length ||
        !_.isEqual(_.last(previousMessages), _.last(this.state.messages)))
    ) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  componentWillUnmount() {
    window.document.removeEventListener("click", this.documentClickHandler);
    $("body").removeAttr("data-clickMode");
    $("body").removeClass("glossStyleNew");
    $("body").removeClass("basicLatinFirstLanguage");

    models.user.off("change:speakingMode", this.checkTTSSupport);
    this.unmounted = true;
  }
}

export default ChatPageView;
